/**
 * ProjectActivityEvent - Display individual SSE event.
 */

import type { JSX } from 'react';
import { Chip } from '@heroui/react';

import type { SSEEvent } from '~/types';

import { formatEventMessage, formatTimestamp, getEventStyle } from './utils';

export function ProjectActivityEvent(props: SSEEvent): JSX.Element {
  const { data, event } = props;

  const { chipColor, icon: Icon, iconColorClass } = getEventStyle(event);

  // Format message based on event type and phase
  const displayMessage = formatEventMessage(props);

  // Get timestamp - available on all event types
  const timestamp = 'timestamp' in data ? formatTimestamp(data.timestamp) : undefined;

  // Type narrowing for progress percentage (ProgressEvent only)
  const progressPercent = event === 'progress' && 'progress' in data ? data.progress : undefined;

  // Type narrowing for error_type (ErrorEvent only)
  const errorType = event === 'error' && 'error_type' in data ? data.error_type : undefined;

  return (
    <div className="p-3 rounded-lg border border-default-200 bg-white/50 dark:border-default-700 dark:bg-default-800/50">
      <div className="flex items-start gap-2">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColorClass}`} />
        <div className="flex-1 min-w-0">
          {displayMessage && <p className="text-sm text-primary">{displayMessage}</p>}
          <div className="flex items-center gap-2 mt-2">
            <Chip color={chipColor}>{event.replace('_', ' ')}</Chip>
            {timestamp && <span className="text-xs text-muted">{timestamp}</span>}
            {progressPercent !== undefined && (
              <span className="text-xs text-muted">{progressPercent}%</span>
            )}
          </div>
          {errorType && <p className="text-xs text-danger-600 mt-1">Error: {errorType}</p>}
        </div>
      </div>
    </div>
  );
}
